import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  CancelToken,
} from 'axios';
import { BrowserUtility } from '@utility/browser-utility';
import { CommonConstant, customErrorStatus } from '@utility/constants/common';
import UserService from './user';

const apiPath = import.meta.env.VITE_API_PATH;
const apiVersion = import.meta.env.VITE_API_VERSION;
const prodAPIPath = import.meta.env.VITE_PROD_API_PATH;

export type ApiError = {
  code: string;
  error: string;
  status: string | number;
};

export type ApiResponse<T = any> = T;

const onSuccess = <T = any>(response: AxiosResponse<T>): T => response.data;

const onError = async (error: any): Promise<never> => {
  let cancelledThroughToken = false;
  if (axios.isCancel(error)) {
    cancelledThroughToken = true;
  }

  if (error.response) {
    // Request was made but server responded with something
    // other than 2xx
    if (error.response.status === 401) {
      const ipAddress = localStorage.getItem(CommonConstant.ipAddress);
      const return_url = BrowserUtility.get('marketplace_return_url');
      window.localStorage.clear();
      if (ipAddress) localStorage.setItem(CommonConstant.ipAddress, ipAddress);
      if (return_url) {
        window.location.href = return_url as string;
      } else {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  }

  if (cancelledThroughToken) {
    return Promise.reject({
      code: customErrorStatus.cancelAxios,
      error: 'Token Expired',
      status: '450',
    } as ApiError);
  }

  return Promise.reject({
    code: error?.response?.data?.code || error?.response?.code || '',
    error: error?.response?.data?.error || error?.response?.data || '',
    status: error?.response?.status || '',
    details: error?.response?.data?.details || '',
  } as ApiError);
};

const refreshToken = async (error: any): Promise<any> => {
  const originalRequest = error.config;
  if (error?.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    const refresh_token = UserService.getRefreshToken();
    const return_url = BrowserUtility.get('marketplace_return_url');
    if (refresh_token) {
      try {
        const params = {
          refresh_token,
        };
        const result = await UserService.refreshToken(params);
        const temp = {
          ...result,
          subscription: undefined,
          user: {
            // The Firebase session is checked against this id (firebase.ts).
            _id: result?.user?._id,
            organization_id: result?.user?.organization_id,
          },
        };
        UserService.storeUser(temp);
        originalRequest.headers.Authorization = `Bearer ${result.access_token}`;
        return originalRequest;
      } catch (error) {
        if (return_url) {
          window.location.href = return_url as string;
        } else {
          window.location.href = '/login';
        }
      }
    } else if (return_url) {
      window.location.href = return_url as string;
    } else {
      window.location.href = '/login';
    }
  }
  return Promise.reject(error);
};

const request = async <T = any>(
  options: AxiosRequestConfig,
  isSecure?: boolean,
  setBaseUrl?: string | null,
  signal?: AbortSignal,
  additonalConfig: AxiosRequestConfig = {},
  additionalHeaders: Record<string, string> = {},
): Promise<T> => {
  const headers: Record<string, string> = {};

  const versionAPI = 'v2';

  const apiUrl = `${apiPath}/${versionAPI}`;

  const currentBaseURL = BrowserUtility.get(CommonConstant.useProd)
    ? `${prodAPIPath}/${apiVersion}`
    : apiUrl;

  if (isSecure) {
    const token = UserService.getToken();
    headers.Authorization = `Bearer ${token}`;
  }

  const client = axios.create({
    baseURL: setBaseUrl || currentBaseURL,
    headers: { ...headers, ...additionalHeaders },
    signal,
    ...additonalConfig,
  });

  client.interceptors.response.use(
    response => response,
    async error => {
      const refreshReq = await refreshToken(error);
      return client(refreshReq);
    },
  );

  return client(options).then(onSuccess).catch(onError) as Promise<T>;
};

const uploadFiles = <T = any>(
  url: string,
  data: any,
  headers: Record<string, string>,
): Promise<T> => {
  const client = axios({
    url,
    method: 'PUT',
    headers: { ...headers },
    data,
  });

  return client.then(onSuccess).catch(onError) as Promise<T>;
};

export class BaseServiceV2 {
  static get = <T = any>(
    url: string,
    isSecure: boolean = true,
    signal?: AbortSignal,
    additonalConfig?: AxiosRequestConfig,
    headers?: Record<string, string>,
  ): Promise<T> =>
    request<T>(
      {
        url,
        method: 'GET',
      },
      isSecure,
      null,
      signal,
      additonalConfig,
      headers,
    );

  static getV2 = <T = any>(
    url: string,
    setBaseUrl?: string,
    isSecure: boolean = true,
  ): Promise<T> =>
    request<T>(
      {
        url,
        method: 'GET',
      },
      isSecure,
      setBaseUrl,
    );

  static post = <T = any>(
    url: string,
    data?: any,
    isSecure: boolean = true,
    setBaseUrl?: string,
    signal?: AbortSignal,
  ): Promise<T> =>
    request<T>(
      {
        url,
        method: 'POST',
        data,
      },
      isSecure,
      setBaseUrl,
      signal,
    );

  static put = <T = any>(
    url: string,
    data?: any,
    isSecure: boolean = true,
    setBaseUrl?: string,
    signal?: AbortSignal,
  ): Promise<T> =>
    request<T>(
      {
        url,
        method: 'PUT',
        data,
      },
      isSecure,
      setBaseUrl,
      signal,
    );

  static remove = <T = any>(
    url: string,
    isSecure: boolean = true,
  ): Promise<T> =>
    request<T>(
      {
        url,
        method: 'DELETE',
      },
      isSecure,
    );

  static upload = <T = any>(
    url: string,
    data: any,
    header: Record<string, string>,
  ): Promise<T> => uploadFiles<T>(url, data, header);
}
